import { Flex, Hide, Image as ChakraImage, Link, Show } from '@chakra-ui/react';
import React, { useState } from 'react';
import Confetti from 'react-confetti';
import { Link as RouterLink } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { ColorModeToggle } from '~/core/routes/dev/ColorModeToggle';
import { colors } from '~/packages/common-frontend/colors';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import adminLogo from '~/packages/common-frontend/images/Admin-Logo-Inverted.svg';
import mainInvertedLogo from '~/packages/common-frontend/images/Logo-Horizontal-Inverted.svg';
import mainLogo from '~/packages/common-frontend/images/Logo-Horizontal-Normal.svg';
import squareLogo from '~/packages/common-frontend/images/Logo-square.svg';
import { EnvironmentTag } from '~/packages/common-frontend/layout/EnvironmentTag';
import { useLayoutContext } from '~/packages/common-frontend/layout/LayoutProvider';
import { UserMenu } from '~/packages/common-frontend/layout/UserMenu';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';

// Exports
type HeaderLogoProps = { isAdmin?: boolean };
export const HeaderLogo = ({ isAdmin = false }: HeaderLogoProps) => {
  const mode = useSimpleColorMode();
  const [showConfetti, setShowConfetti] = useState(false);
  const coreLogo = mode(mainLogo, mainInvertedLogo);
  const logoSVG = new Image();
  logoSVG.src = squareLogo;
  return (
    <>
      <Link as={RouterLink} flexShrink={0} to="/">
        <ChakraImage
          alt="Logo"
          id="app-header-logo"
          cursor="pointer"
          height="60px"
          src={isAdmin ? adminLogo : coreLogo}
          onDoubleClick={() => setShowConfetti(true)}
          width={
            isAdmin
              ? { base: '120px', sm: '140px', md: '185px' }
              : { base: '65px', sm: '75px', md: '100px' }
          }
        />
      </Link>
      {showConfetti && (
        <Confetti
          recycle={false}
          drawShape={(ctx) => {
            ctx.drawImage(logoSVG, 0, 0);
          }}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
    </>
  );
};

type HeaderRowProps = {
  children: React.ReactNode;
  FlexProps?: Partial<React.ComponentProps<typeof Flex>>;
};
export const HeaderRow = ({ children, FlexProps }: HeaderRowProps) => {
  const mode = useSimpleColorMode();
  return (
    <Flex
      alignItems="center"
      backgroundColor={mode('white', colors.gray[950])}
      borderBottom={`1px solid ${mode(colors.gray[50], colors.gray[900])}`}
      boxShadow="0 0 4px hsl(0deg 0% 76% / 35%)"
      direction="row"
      flexShrink={0}
      gap="1rem"
      height="5.25rem"
      justifyContent="space-between"
      padding={{ base: '15px 20px', lg: '15px 30px' }}
      position="fixed"
      zIndex="sticky"
      width="100%"
      {...FlexProps}
    >
      {children}
    </Flex>
  );
};

type DefaultHeaderProps = {
  isAdmin?: boolean;
  HeaderRowProps?: Partial<React.ComponentProps<typeof HeaderRow>>;
};
export const AppHeader = ({
  isAdmin = false,
  HeaderRowProps,
}: DefaultHeaderProps) => {
  const { setGlobalNavOpen } = useLayoutContext();
  return (
    <HeaderRow
      {...HeaderRowProps}
      FlexProps={{
        zIndex: 'calc(var(--chakra-zIndices-sticky) + 1)',
        ...HeaderRowProps?.FlexProps,
      }}
    >
      <Flex direction="row" gap="1rem" alignItems="center">
        <HeaderLogo isAdmin={isAdmin} />
        <EnvironmentTag />
        {!commonUtils.EnvUtils.isCustomerFacing() && <ColorModeToggle />}
      </Flex>
      <Flex direction="row" gap="1rem" alignItems="center">
        <Show above="lg">
          <UserMenu isAdmin={isAdmin} />
        </Show>
        <Hide above="lg">
          <IconButtonV2
            aria-label="Menu"
            icon={<Icon icon="bars" size="1rem" />}
            variant="v2-ghost"
            colorScheme="v2-aurora"
            onClick={() => setGlobalNavOpen((prev) => !prev)}
          />
        </Hide>
      </Flex>
    </HeaderRow>
  );
};
